import React, { useEffect, useState } from "react";
import CopyButton from "./CopyButton";
import Switch from "../automations/Switch";

/*
 * MEETING TYPES on the Page tab. Asked for 2026-09-30: "We only built the
 * meeting for leads. We need another for friendly chat, interview (not
 * immediately), and any others." Ruled the same day:
 *
 *   - private links only: each type lives at /book/<link> and nothing public
 *     links to it
 *   - never in the CRM: a friendly chat creates no contact and no lead
 *   - its own length, his one set of bookable hours
 *
 * The intro call is the first row and is not editable here: it is the page at
 * /book, set up by everything above this section.
 */

const SLUG = /^[a-z0-9-]{2,40}$/;
const RESERVED = new Set(["booked", "manage", "read", "preview", "intro"]);

function slugify(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

function problems(types) {
  const out = {};
  const seen = new Map();
  types.forEach((t, i) => {
    const errs = [];
    if (!t.name.trim()) errs.push("Give it a name.");
    if (!SLUG.test(t.slug)) errs.push("The link needs 2 to 40 lowercase letters, numbers or dashes.");
    else if (RESERVED.has(t.slug)) errs.push(`"${t.slug}" is already a page of its own.`);
    else if (seen.has(t.slug)) errs.push("Another meeting type already uses this link.");
    seen.set(t.slug, i);
    if (!(t.minutes >= 10 && t.minutes <= 180)) errs.push("Length is 10 to 180 minutes.");
    if (!t.title.trim()) errs.push("Give the invite a title.");
    if (!t.headline.trim()) errs.push("Give the page a headline.");
    if (t.kind === "in_person") {
      const away = String(t.country || "Israel").trim().toLowerCase() !== "israel";
      if (!String(t.country || "").trim()) errs.push("Say which country you're in.");
      if (away && !String(t.leave_from || "").trim()) errs.push("Away from Israel, say where you'll be leaving from.");
      if (!away && t.enabled !== false && !String(t.office_address || "").trim() && !String(t.leave_from || "").trim()) {
        errs.push("Add your office address before switching this on.");
      }
      if (!(Number(t.park_minutes) >= 30 && Number(t.park_minutes) <= 90)) errs.push("Parking is 30 to 90 minutes.");
      if (!(Number(t.max_travel_minutes) >= 15 && Number(t.max_travel_minutes) <= 300)) errs.push("The longest drive is 15 to 300 minutes.");
    }
    if (errs.length) out[i] = errs;
  });
  return out;
}

const IN_PERSON_NEW = {
  name: "In person",
  slug: "meet",
  minutes: 60,
  kind: "in_person",
  title: "{{name}} <> {{host}} (in person)",
  headline: "Hey. Let's meet in person. Pick a time.",
  enabled: false,
  country: "Israel",
  office_address: "",
  leave_from: "",
  park_minutes: 30,
  max_travel_minutes: 120,
};

/*
 * IN PERSON (2026-10-07): Israel only unless he's travelling, at his office or
 * at theirs. At theirs the drive there (traffic, plus parking) and back is
 * blocked on his calendar. Which of the two a link offers is his: three links.
 */
function InPersonFields({ t, patch, link, travelSource }) {
  const away = String(t.country || "Israel").trim().toLowerCase() !== "israel";
  return (
    <div className="crm-bktype__grid crm-bktype__inperson">
      <label>
        <span className="crm-bkmono">Where you are</span>
        <input className="crm-input" value={t.country || ""} maxLength={60} placeholder="Israel" onChange={(e) => patch({ country: e.target.value })} />
        <span className="crm-hint">
          {away ? "Travelling: only their place is offered, in this country's time." : "Israel only. Change this when you travel."}
        </span>
      </label>
      <label>
        <span className="crm-bkmono">Your office</span>
        <input
          className="crm-input"
          value={t.office_address || ""}
          maxLength={200}
          placeholder="Street and city"
          disabled={away}
          onChange={(e) => patch({ office_address: e.target.value })}
        />
        <span className="crm-hint">{away ? "Not offered while you're away." : "Shown to anyone meeting you there."}</span>
      </label>
      <label>
        <span className="crm-bkmono">Driving from</span>
        <input
          className="crm-input"
          value={t.leave_from || ""}
          maxLength={200}
          placeholder={away ? "Your hotel's address" : "Blank is your office"}
          onChange={(e) => patch({ leave_from: e.target.value })}
        />
      </label>
      <label>
        <span className="crm-bkmono">To park</span>
        <span className="crm-bktype__minutes">
          <input
            className="crm-input crm-input--narrow"
            inputMode="numeric"
            value={t.park_minutes}
            onChange={(e) => patch({ park_minutes: parseInt(e.target.value.replace(/\D/g, "") || "0", 10) })}
          />
          <span className="crm-hint">minutes, at least 30</span>
        </span>
      </label>
      <label>
        <span className="crm-bkmono">Longest drive</span>
        <span className="crm-bktype__minutes">
          <input
            className="crm-input crm-input--narrow"
            inputMode="numeric"
            value={t.max_travel_minutes}
            onChange={(e) => patch({ max_travel_minutes: parseInt(e.target.value.replace(/\D/g, "") || "0", 10) })}
          />
          <span className="crm-hint">minutes; further than this is turned away</span>
        </span>
      </label>
      <div>
        <span className="crm-bkmono">Links</span>
        {[
          ["They choose", ""],
          ["At your office", "?at=office"],
          ["At their place", "?at=theirs"],
        ]
          .filter(([, q]) => !(away && q === "?at=office"))
          .map(([label, q]) => (
            <span key={label} className="crm-bktype__link">
              <span className="crm-hint">{label}</span>
              <CopyButton text={link ? `${link}${q}` : null} />
            </span>
          ))}
        <span className="crm-hint">
          At their place, the drive both ways (traffic, plus parking) is blocked on your calendar.
          {travelSource === "google" ? " Drive times come from Google Maps." : " Drive times are estimated from distance and time of day until a Google Maps key is set."}
        </span>
      </div>
    </div>
  );
}

export default function MeetingTypes({ types: saved, introMinutes, baseUrl, save, travelSource = "estimate" }) {
  const [types, setTypes] = useState(saved || []);

  // Adopt the keys the server gives new types, so later saves keep them.
  useEffect(() => {
    setTypes((local) => local.map((t, i) => (!t.key && saved[i] && saved[i].slug === t.slug ? { ...t, key: saved[i].key } : t)));
  }, [saved]);

  const errors = problems(types);

  const change = (next) => {
    setTypes(next);
    if (!Object.keys(problems(next)).length) {
      save(
        "types",
        next.map((t) => ({
          ...(t.key ? { key: t.key } : {}),
          name: t.name,
          slug: t.slug,
          minutes: Number(t.minutes),
          title: t.title,
          headline: t.headline,
          enabled: t.enabled !== false,
          ...(t.for_clients && t.kind !== "in_person" ? { for_clients: true } : {}),
          ...(t.kind === "in_person"
            ? {
                kind: "in_person",
                country: String(t.country || "Israel").trim(),
                office_address: String(t.office_address || "").trim(),
                leave_from: String(t.leave_from || "").trim(),
                park_minutes: Number(t.park_minutes),
                max_travel_minutes: Number(t.max_travel_minutes),
              }
            : {}),
        }))
      );
    }
  };
  const patch = (i, fields) => change(types.map((t, j) => (j === i ? { ...t, ...fields } : t)));
  const link = (slug) => (baseUrl ? `${baseUrl}/book/${slug}` : null);

  return (
    <section className="crm-card crm-bkbox crm-bktypes">
      <div className="crm-bkreads__head">
        <h3>Meeting types</h3>
        <span className="crm-hint">{types.length + 1} types</span>
      </div>
      <p className="crm-hint">
        Every type uses your bookable hours from the Calendar tab. Only intro calls touch the CRM: anything else is just a
        calendar invite, never a lead. Each type has its own private link that nothing public points to.
      </p>

      <div className="crm-bktype crm-bktype--fixed">
        <div className="crm-bktype__head">
          <b>Intro call</b>
          <span className="crm-bkchip crm-bkchip--plan">Leads</span>
          <span className="crm-hint">{introMinutes} min · set up above</span>
        </div>
        <span className="crm-bktype__link">
          <code>{baseUrl ? `${baseUrl.replace(/^https?:\/\//, "")}/book` : "/book"}</code>
          <CopyButton text={baseUrl ? `${baseUrl}/book` : null} />
        </span>
      </div>

      {types.map((t, i) => (
        <div key={t.key || `new-${i}`} className={`crm-bktype${t.enabled === false ? " is-off" : ""}`}>
          <div className="crm-bktype__head">
            <input
              className="crm-input crm-bktype__name"
              value={t.name}
              maxLength={60}
              placeholder="Name, e.g. Friendly chat"
              onChange={(e) => {
                const name = e.target.value;
                // A new type's link follows its name until the link is edited by hand.
                const followsName = !t.key && (!t.slug || t.slug === slugify(t.name));
                patch(i, { name, ...(followsName ? { slug: slugify(name) } : {}) });
              }}
            />
            <label className="crm-auto-switch">
              <span>{t.enabled === false ? "Off" : "On"}</span>
              <Switch on={t.enabled !== false} label="Switch this meeting type on or off" onChange={(on) => patch(i, { enabled: on })} />
            </label>
            <button type="button" className="crm-bkicon" onClick={() => change(types.filter((_, j) => j !== i))} aria-label="Remove meeting type">
              ×
            </button>
          </div>
          <div className="crm-bktype__grid">
            <label>
              <span className="crm-bkmono">Link</span>
              <span className="crm-bktype__slug">
                <span className="crm-hint">{baseUrl ? `${baseUrl.replace(/^https?:\/\//, "")}/book/` : "/book/"}</span>
                <input
                  className="crm-input"
                  value={t.slug}
                  maxLength={40}
                  onChange={(e) => patch(i, { slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                />
                <CopyButton text={SLUG.test(t.slug) ? link(t.slug) : null} />
              </span>
            </label>
            <label>
              <span className="crm-bkmono">Length</span>
              <span className="crm-bktype__minutes">
                <input
                  className="crm-input crm-input--narrow"
                  inputMode="numeric"
                  value={t.minutes}
                  onChange={(e) => patch(i, { minutes: parseInt(e.target.value.replace(/\D/g, "") || "0", 10) })}
                />
                <span className="crm-hint">minutes</span>
              </span>
            </label>
            <label>
              <span className="crm-bkmono">Page headline</span>
              <input
                className="crm-input"
                value={t.headline}
                maxLength={140}
                placeholder="Hey. Let's catch up. Pick a time."
                onChange={(e) => patch(i, { headline: e.target.value })}
              />
            </label>
            <label>
              <span className="crm-bkmono">Invite title</span>
              <input
                className="crm-input"
                value={t.title}
                maxLength={120}
                placeholder="Catch-up: {{name}} <> {{host}}"
                onChange={(e) => patch(i, { title: e.target.value })}
              />
              <span className="crm-hint">
                {"{{name}}"} is them, {"{{host}}"} is you. Avoid the word "Remotion" here.
              </span>
            </label>
            {t.kind !== "in_person" ? (
              <label className="crm-bktype__check">
                <input
                  type="checkbox"
                  checked={!!t.for_clients}
                  onChange={(e) =>
                    change(types.map((x, j) => (j === i ? { ...x, for_clients: e.target.checked } : { ...x, for_clients: e.target.checked ? false : x.for_clients })))
                  }
                />
                <span>
                  For clients. A client's record copies this link with their company in it, and the page and invite read "Remotion / their
                  company".
                </span>
              </label>
            ) : null}
          </div>
          {t.kind === "in_person" ? <InPersonFields t={t} patch={(fields) => patch(i, fields)} link={SLUG.test(t.slug) ? link(t.slug) : null} travelSource={travelSource} /> : null}
          {errors[i] ? (
            <ul className="crm-bktype__errors">
              {errors[i].map((e) => (
                <li key={e} className="crm-hint crm-hint--warn">
                  {e}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ))}

      {types.length < 12 && !types.some((t) => t.kind === "in_person") ? (
        <button
          type="button"
          className="crm-linkact"
          onClick={() => setTypes([...types, { ...IN_PERSON_NEW }])}
        >
          + Add in-person meeting
        </button>
      ) : null}
      {types.length < 12 ? (
        <button
          type="button"
          className="crm-linkact"
          onClick={() =>
            setTypes([
              ...types,
              { name: "", slug: "", minutes: 30, title: "{{name}} <> {{host}}", headline: "Hey. Pick a time.", enabled: true },
            ])
          }
        >
          + Add meeting type
        </button>
      ) : null}
    </section>
  );
}
